import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import Stack from "@mui/material/Stack"
import { loginUserSchema } from "validations/auth"
import { useAuth } from "context/AuthContext"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { SERVER_ERROR } from "constants/errors"
import InvisibleIcon from "components/atoms/_icons/InvisibleIcon"
import VisibleIcon from "components/atoms/_icons/VisibleIcon"
import TextField from "components/atoms/TextField"
import CTA from "components/atoms/CTA"
import LoginRegisterQuestionLink from "components/molecules/LoginRegisterQuestionLink"
import ForgetPasswordQuestion from "components/molecules/ForgetPasswordQuestion"

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { login, isUserLoginLoading } = useAuth()

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, dirtyFields }
  } = useForm<loginRequestPayload>({
    resolver: zodResolver(loginUserSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: {
      username: "",
      password: ""
    }
  })

  useEffect(() => {
    if (!Object.keys(errors).length) return
    console.log(errors)
  }, [errors])

  async function loginUser(value: loginRequestPayload) {
    try {
      await login(value)
    } catch (err) {
      console.error("Error happened in loginUser method. Error:", err)
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: SERVER_ERROR,
        show: true,
        status: 500,
        message: `general.${SERVER_ERROR}`
      }))
    }
  }

  function togglePasswordVisibility(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault()
    event.stopPropagation()
    setShowPassword(!showPassword)
  }

  return (
    <form onSubmit={handleSubmit(loginUser)} style={{ width: "100%" }}>
      <Stack width="100%" marginTop={4} flexGrow={1}>
        <TextField
          id="username"
          label={t("auth.username")}
          required={true}
          register={register}
          setValue={setValue}
          error={errors.username}
          direction={"rtl"}
        />
        <TextField
          id="password"
          label={t("auth.password")}
          type={showPassword ? "text" : "password"}
          required={true}
          startAdornmentIcon={showPassword ? <VisibleIcon/> : <InvisibleIcon/>}
          onIconClick={togglePasswordVisibility}
          register={register}
          setValue={setValue}
          error={errors.password}
          direction={"rtl"}
        />
        <ForgetPasswordQuestion/>
      </Stack>
      <CTA
        variant="contained"
        type="submit"
        disabled={Object.keys(dirtyFields).length !== 2 || isUserLoginLoading}
        loading={isUserLoginLoading}
        onClick={handleSubmit(loginUser)}
      >
        {t("auth.login")}
      </CTA>
      <LoginRegisterQuestionLink
        mainQuestion={t("auth.dontHaveAnAccountQuestion")}
        link={{
          label: t("auth.register"),
          href: "/register"
        }}
      />
    </form>
  )
}
